import React, { useState, useEffect } from 'react';
import { useLandingData } from '@/context/LandingDataContext';
import {
  Lock,
  Unlock,
  X,
  Save,
  Upload,
  Plus,
  Trash,
  LogOut,
  Edit,
  Check,
  Eye,
  AlertCircle,
  ArrowLeft,
  Globe,
  Sparkles,
  ShieldCheck,
  Leaf,
  Star
} from 'lucide-react';
import { toast } from 'sonner';

// Preview Components
import { Hero } from "@/components/landing/Hero";
import { Products } from "@/components/landing/Products";
import { Benefits } from "@/components/landing/Benefits";
import { About } from "@/components/landing/About";
import { Testimonials } from "@/components/landing/Testimonials";

export const AdminPanel = () => {
  const {
    content,
    isAdmin,
    login,
    logout,
    saveContent,
    uploadImageFile,
    refreshContent,
    setTempContent
  } = useLandingData();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('hero');
  const [uploadingField, setUploadingField] = useState(null);

  // Local draft state for live-preview and editing
  const [draftContent, setDraftContent] = useState(null);

  // Initialize draftContent when content loads
  useEffect(() => {
    if (content && !draftContent) {
      setDraftContent(JSON.parse(JSON.stringify(content)));
    }
  }, [content, draftContent]);

  // Synchronize draft to live state for context dynamic preview
  const updateDraft = (path, value) => {
    if (!draftContent) return;
    const newDraft = JSON.parse(JSON.stringify(draftContent)); // Deep copy
    const parts = path.split('.');
    let current = newDraft;
    for (let i = 0; i < parts.length - 1; i++) {
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
    setDraftContent(newDraft);

    // Dynamic live preview update - update context on the fly!
    if (setTempContent) {
      setTempContent(newDraft);
    }
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    const success = login(username, password);
    if (success) {
      toast.success('Login Admin Berhasil!', {
        description: 'Anda sekarang dapat mengedit konten landing page secara langsung.'
      });
    } else {
      toast.error('Login Gagal', {
        description: 'Username atau password salah.'
      });
    }
  };

  const handleSave = async () => {
    const res = await saveContent(draftContent);
    if (res.success) {
      toast.success('Semua Perubahan Disimpan!', {
        description: 'Konten landing page telah berhasil diperbarui di server.'
      });
    } else {
      toast.warning('Penyimpanan Terbatas', {
        description: res.message || 'Gagal menyimpan ke server.'
      });
    }
  };

  const handleCancel = () => {
    const original = JSON.parse(JSON.stringify(content));
    setDraftContent(original);
    if (setTempContent) {
      setTempContent(original);
    }
    toast.info('Perubahan draf dibatalkan.');
  };

  const handleLogout = () => {
    logout();
    setDraftContent(null);
    refreshContent();
    toast.info('Anda telah keluar dari panel admin.');
  };

  // Safe file upload handler
  const handleFileUpload = async (e, fieldPath) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingField(fieldPath);
    toast.info('Mengunggah gambar...');

    try {
      const url = await uploadImageFile(file);
      updateDraft(fieldPath, url);
      toast.success('Gambar berhasil diunggah!');
    } catch (err) {
      console.error(err);
      toast.error('Gagal mengunggah gambar', {
        description: typeof err === 'string' ? err : err.message
      });
    } finally {
      setUploadingField(null);
    }
  };

  const addProduct = () => {
    const newProduct = {
      id: `product-${Date.now()}`,
      name: 'Produk Baru',
      category: 'Skincare',
      tagline: 'Slogan produk mewah Anda.',
      image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&q=80&w=400',
      description: 'Deskripsi lengkap produk baru Anda yang elegan dan detail.',
      benefits: ['Manfaat pertama produk', 'Manfaat kedua produk']
    };
    const products = [...draftContent.products, newProduct];
    updateDraft('products', products);
    toast.success('Produk baru ditambahkan ke daftar draft.');
  };

  const removeProduct = (idx) => {
    const products = draftContent.products.filter((_, i) => i !== idx);
    updateDraft('products', products);
    toast.info('Produk dihapus dari draft.');
  };

  const addTestimonial = () => {
    const newTestimonial = {
      quote: 'Review baru yang sangat memuaskan dari pelanggan kami.',
      name: 'Nama Pelanggan',
      role: 'Pelanggan Setia'
    };
    const testimonials = [...draftContent.testimonials, newTestimonial];
    updateDraft('testimonials', testimonials);
    toast.success('Testimoni baru ditambahkan.');
  };

  const removeTestimonial = (idx) => {
    const testimonials = draftContent.testimonials.filter((_, i) => i !== idx);
    updateDraft('testimonials', testimonials);
    toast.info('Testimoni dihapus.');
  };

  // Navigate to live landing page
  const navigateToHome = () => {
    // Restore clean state from server
    if (setTempContent) {
      setTempContent(content);
    }
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new Event('popstate'));
  };

  // Helper component to create beautiful input groups
  const InputGroup = ({ label, value, onChange, type = 'text', placeholder = '' }) => (
    <div className="mb-4">
      <label className="block text-xs font-medium tracking-wide uppercase text-[#8A6E52] mb-1.5">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#FAF8F5] border border-[#E8E3D9] text-[#1A1A1A] text-sm px-4 py-2.5 rounded-md focus:outline-none focus:border-[#BCA38F] focus:ring-1 focus:ring-[#BCA38F] transition-all"
      />
    </div>
  );

  const TextareaGroup = ({ label, value, onChange, rows = 3, placeholder = '' }) => (
    <div className="mb-4">
      <label className="block text-xs font-medium tracking-wide uppercase text-[#8A6E52] mb-1.5">{label}</label>
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full bg-[#FAF8F5] border border-[#E8E3D9] text-[#1A1A1A] text-sm px-4 py-2.5 rounded-md focus:outline-none focus:border-[#BCA38F] focus:ring-1 focus:ring-[#BCA38F] transition-all resize-none"
      />
    </div>
  );

  const ImageUploaderGroup = ({ label, value, onChange, fieldPath }) => (
    <div className="mb-4">
      <label className="block text-xs font-medium tracking-wide uppercase text-[#8A6E52] mb-1.5">{label}</label>
      <div className="flex gap-3 items-center">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="flex-1 bg-[#FAF8F5] border border-[#E8E3D9] text-[#1A1A1A] text-xs px-3 py-2.5 rounded-md focus:outline-none focus:border-[#BCA38F]"
        />
        <label className="relative shrink-0 flex items-center justify-center h-[38px] px-4 rounded-md border border-[#BCA38F] text-[#8A6E52] bg-[#FDFBF7] text-xs font-medium cursor-pointer hover:bg-[#BCA38F] hover:text-white transition-all duration-300">
          {uploadingField === fieldPath ? (
            <div className="h-4 w-4 border-2 border-[#8A6E52] border-t-transparent animate-spin rounded-full" />
          ) : (
            <>
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              Unggah
            </>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileUpload(e, fieldPath)}
            disabled={uploadingField === fieldPath}
            className="hidden"
          />
        </label>
      </div>
      {value && (
        <div className="mt-2 relative inline-block rounded overflow-hidden border border-[#E8E3D9] bg-[#FAF8F5]">
          <img src={value} alt="Preview" className="h-14 w-auto object-contain p-1" />
        </div>
      )}
    </div>
  );

  // --- RENDERING IF NOT LOGGED IN ---
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex flex-col items-center justify-center p-4 relative overflow-hidden">
        {/* Soft luxury glow background effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#BCA38F]/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#8A6E52]/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="w-full max-w-md bg-[#FDFBF7] rounded-lg shadow-xl border border-[#E8E3D9] overflow-hidden z-10 transition-all duration-500">
          <div className="p-8 border-b border-[#E8E3D9] text-center bg-[#FAF8F5]">
            <h2 className="font-serif-display text-3xl tracking-wide text-[#1A1A1A]">DA&apos;VM</h2>
            <p className="text-xs uppercase tracking-[0.25em] text-[#8A6E52] mt-1.5">Administrator Control Panel</p>
          </div>
          <form onSubmit={handleLoginSubmit} className="p-8 space-y-5">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-[#8A6E52] mb-1.5">
                Email / Username
              </label>
              <input
                type="email"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin@email.com"
                className="w-full bg-[#FAF8F5] border border-[#E8E3D9] text-sm px-4 py-3 rounded-md focus:outline-none focus:border-[#BCA38F] focus:ring-1 focus:ring-[#BCA38F] transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-[#8A6E52] mb-1.5">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[#FAF8F5] border border-[#E8E3D9] text-sm px-4 py-3 rounded-md focus:outline-none focus:border-[#BCA38F] focus:ring-1 focus:ring-[#BCA38F] transition-all"
              />
            </div>
            <button
              type="submit"
              className="w-full h-12 bg-[#1A1A1A] text-white hover:bg-[#8A6E52] transition-colors rounded-md text-xs font-bold tracking-widest uppercase mt-6 shadow-md"
            >
              Masuk ke Panel
            </button>
            <button
              type="button"
              onClick={navigateToHome}
              className="w-full h-12 border border-[#E8E3D9] text-[#5C5C5C] hover:bg-[#FAF8F5] transition-colors rounded-md text-xs font-semibold tracking-widest uppercase flex items-center justify-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Kembali ke Beranda
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- RENDERING DUAL PANEL WORKSPACE IF LOGGED IN ---
  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[#FAF8F5] text-[#1A1A1A]">
      
      {/* LEFT EDITOR PANEL */}
      <div className="w-full lg:w-[540px] xl:w-[580px] shrink-0 border-r border-[#E8E3D9] flex flex-col bg-[#FDFBF7] h-full z-10 shadow-lg">
        
        {/* Header */}
        <div className="p-6 border-b border-[#E8E3D9] bg-[#FAF8F5] flex justify-between items-center shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <h2 className="font-serif-display text-2xl text-[#1A1A1A]">Workspace Content</h2>
            </div>
            <p className="text-xs text-[#8A6E52] mt-0.5">Edit konten langsung dan simpan secara instan</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={navigateToHome}
              className="text-xs text-[#5C5C5C] hover:text-[#1A1A1A] flex items-center gap-1 font-semibold transition-colors bg-[#FAF8F5] border border-[#E8E3D9] px-3 py-1.5 rounded"
              title="Kembali ke Landing Page"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Beranda
            </button>
            <button
              onClick={handleLogout}
              className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 font-semibold transition-colors"
              title="Log out"
            >
              <LogOut className="h-3.5 w-3.5" />
              Keluar
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-[#E8E3D9] bg-[#FAF8F5] overflow-x-auto scrollbar-none shrink-0 px-2">
          {[
            { id: 'hero', name: 'Hero' },
            { id: 'products', name: 'Produk' },
            { id: 'benefits', name: 'Manfaat' },
            { id: 'about', name: 'Tentang' },
            { id: 'testimonials', name: 'Testimoni' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3.5 px-4 text-xs font-semibold tracking-wider uppercase border-b-2 shrink-0 transition-all ${
                activeTab === tab.id
                  ? 'border-[#8A6E52] text-[#8A6E52] bg-[#FDFBF7]'
                  : 'border-transparent text-[#1A1A1A]/60 hover:text-[#1A1A1A]'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>

        {/* Scrollable Form Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {draftContent ? (
            <>
              {/* --- HERO TAB --- */}
              {activeTab === 'hero' && (
                <div className="space-y-4">
                  <h3 className="font-serif-display text-lg text-[#1A1A1A] border-b border-[#E8E3D9] pb-2 mb-4">
                    Bagian Beranda (Hero Banner)
                  </h3>
                  <InputGroup
                    label="Overline (Koleksi)"
                    value={draftContent.hero.overline}
                    onChange={(val) => updateDraft('hero.overline', val)}
                  />
                  <InputGroup
                    label="Judul Utama"
                    value={draftContent.hero.title}
                    onChange={(val) => updateDraft('hero.title', val)}
                  />
                  <InputGroup
                    label="Judul Cetak Miring"
                    value={draftContent.hero.italicTitle}
                    onChange={(val) => updateDraft('hero.italicTitle', val)}
                  />
                  <TextareaGroup
                    label="Sub-Deskripsi"
                    value={draftContent.hero.subtitle}
                    onChange={(val) => updateDraft('hero.subtitle', val)}
                  />
                  <ImageUploaderGroup
                    label="Foto Background Utama"
                    value={draftContent.hero.bgImage}
                    fieldPath="hero.bgImage"
                    onChange={(val) => updateDraft('hero.bgImage', val)}
                  />

                  <h4 className="text-xs font-semibold uppercase tracking-wider text-[#8A6E52] mt-6 mb-3">
                    Statistik Banner
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {draftContent.hero.stats?.map((stat, idx) => (
                      <div key={idx} className="p-3 bg-[#FAF8F5] border border-[#E8E3D9] rounded-md">
                        <InputGroup
                          label={`Statistik ${idx + 1} - Angka`}
                          value={stat.num}
                          onChange={(val) => {
                            const stats = [...draftContent.hero.stats];
                            stats[idx].num = val;
                            updateDraft('hero.stats', stats);
                          }}
                        />
                        <InputGroup
                          label={`Statistik ${idx + 1} - Keterangan`}
                          value={stat.label}
                          onChange={(val) => {
                            const stats = [...draftContent.hero.stats];
                            stats[idx].label = val;
                            updateDraft('hero.stats', stats);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* --- PRODUCTS TAB --- */}
              {activeTab === 'products' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-serif-display text-lg text-[#1A1A1A] border-b border-[#E8E3D9] pb-2 mb-4">
                      Header Bagian Produk
                    </h3>
                    <InputGroup
                      label="Overline"
                      value={draftContent.productsSection?.overline}
                      onChange={(val) => updateDraft('productsSection.overline', val)}
                    />
                    <InputGroup
                      label="Judul Utama"
                      value={draftContent.productsSection?.title}
                      onChange={(val) => updateDraft('productsSection.title', val)}
                    />
                    <InputGroup
                      label="Judul Miring"
                      value={draftContent.productsSection?.italicTitle}
                      onChange={(val) => updateDraft('productsSection.italicTitle', val)}
                    />
                    <TextareaGroup
                      label="Deskripsi Singkat"
                      value={draftContent.productsSection?.description}
                      onChange={(val) => updateDraft('productsSection.description', val)}
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center border-b border-[#E8E3D9] pb-2 mb-4">
                      <h3 className="font-serif-display text-lg text-[#1A1A1A]">Daftar Produk ({draftContent.products.length})</h3>
                      <button
                        type="button"
                        onClick={addProduct}
                        className="text-xs bg-[#8A6E52] text-white px-3 py-1.5 rounded flex items-center gap-1 hover:bg-[#1A1A1A] transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" /> Tambah
                      </button>
                    </div>

                    <div className="space-y-6">
                      {draftContent.products.map((p, idx) => (
                        <div key={p.id || idx} className="p-4 bg-[#FAF8F5] border border-[#E8E3D9] rounded-lg relative">
                          <button
                            type="button"
                            onClick={() => removeProduct(idx)}
                            className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                            title="Hapus Produk"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                          <h4 className="font-serif-display text-md text-[#1A1A1A] mb-3">Produk #{idx + 1}: {p.name}</h4>
                          <InputGroup
                            label="Nama Produk"
                            value={p.name}
                            onChange={(val) => {
                              const products = [...draftContent.products];
                              products[idx].name = val;
                              updateDraft('products', products);
                            }}
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <InputGroup
                              label="Kategori"
                              value={p.category}
                              onChange={(val) => {
                                const products = [...draftContent.products];
                                products[idx].category = val;
                                updateDraft('products', products);
                              }}
                            />
                            <InputGroup
                              label="Tagline"
                              value={p.tagline}
                              onChange={(val) => {
                                const products = [...draftContent.products];
                                products[idx].tagline = val;
                                updateDraft('products', products);
                              }}
                            />
                          </div>
                          <TextareaGroup
                            label="Deskripsi"
                            value={p.description}
                            onChange={(val) => {
                              const products = [...draftContent.products];
                              products[idx].description = val;
                              updateDraft('products', products);
                            }}
                          />
                          <ImageUploaderGroup
                            label="Foto Produk"
                            value={p.image}
                            fieldPath={`products.${idx}.image`}
                            onChange={(val) => {
                              const products = [...draftContent.products];
                              products[idx].image = val;
                              updateDraft('products', products);
                            }}
                          />

                          {/* Product Benefits */}
                          <div>
                            <label className="block text-xs font-medium uppercase tracking-wide text-[#8A6E52] mb-1.5">
                              Manfaat Produk (1 baris per manfaat)
                            </label>
                            <textarea
                              value={p.benefits?.join('\n') || ''}
                              onChange={(e) => {
                                const products = [...draftContent.products];
                                products[idx].benefits = e.target.value.split('\n').filter(x => x.trim() !== '');
                                updateDraft('products', products);
                              }}
                              rows={3}
                              placeholder="Tuliskan setiap manfaat di baris baru..."
                              className="w-full bg-white border border-[#E8E3D9] text-[#1A1A1A] text-sm px-3 py-2 rounded focus:outline-none"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* --- BENEFITS TAB --- */}
              {activeTab === 'benefits' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-serif-display text-lg text-[#1A1A1A] border-b border-[#E8E3D9] pb-2 mb-4">
                      Header Bagian Filosofi & Manfaat
                    </h3>
                    <InputGroup
                      label="Overline"
                      value={draftContent.benefitsSection?.overline}
                      onChange={(val) => updateDraft('benefitsSection.overline', val)}
                    />
                    <InputGroup
                      label="Judul Utama"
                      value={draftContent.benefitsSection?.title}
                      onChange={(val) => updateDraft('benefitsSection.title', val)}
                    />
                    <InputGroup
                      label="Judul Miring"
                      value={draftContent.benefitsSection?.italicTitle}
                      onChange={(val) => updateDraft('benefitsSection.italicTitle', val)}
                    />
                    <TextareaGroup
                      label="Keterangan Samping"
                      value={draftContent.benefitsSection?.description}
                      onChange={(val) => updateDraft('benefitsSection.description', val)}
                    />
                  </div>

                  <div>
                    <h3 className="font-serif-display text-lg text-[#1A1A1A] border-b border-[#E8E3D9] pb-2 mb-4">
                      4 Pilar Filosofi Utama
                    </h3>
                    <div className="space-y-4">
                      {draftContent.benefits.map((b, idx) => (
                        <div key={idx} className="p-4 bg-[#FAF8F5] border border-[#E8E3D9] rounded-lg">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-medium text-sm text-[#8A6E52]">Pilar #{idx + 1}</h4>
                            <select
                              value={b.icon}
                              onChange={(e) => {
                                const benefits = [...draftContent.benefits];
                                benefits[idx].icon = e.target.value;
                                updateDraft('benefits', benefits);
                              }}
                              className="text-xs bg-[#FDFBF7] border border-[#E8E3D9] rounded px-2 py-1 focus:outline-none"
                            >
                              <option value="Sparkles">Bintang (Sparkles)</option>
                              <option value="ShieldCheck">Perisai (ShieldCheck)</option>
                              <option value="Leaf">Daun (Leaf)</option>
                              <option value="Star">Bintang Utama (Star)</option>
                            </select>
                          </div>
                          <InputGroup
                            label="Nama Pilar"
                            value={b.title}
                            onChange={(val) => {
                              const benefits = [...draftContent.benefits];
                              benefits[idx].title = val;
                              updateDraft('benefits', benefits);
                            }}
                          />
                          <TextareaGroup
                            label="Keterangan Pilar"
                            value={b.description}
                            onChange={(val) => {
                              const benefits = [...draftContent.benefits];
                              benefits[idx].description = val;
                              updateDraft('benefits', benefits);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* --- ABOUT TAB --- */}
              {activeTab === 'about' && (
                <div className="space-y-4">
                  <h3 className="font-serif-display text-lg text-[#1A1A1A] border-b border-[#E8E3D9] pb-2 mb-4">
                    Bagian Tentang Kami
                  </h3>
                  <InputGroup
                    label="Overline"
                    value={draftContent.about.overline}
                    onChange={(val) => updateDraft('about.overline', val)}
                  />
                  <InputGroup
                    label="Judul Utama"
                    value={draftContent.about.title}
                    onChange={(val) => updateDraft('about.title', val)}
                  />
                  <InputGroup
                    label="Judul Miring"
                    value={draftContent.about.italicTitle}
                    onChange={(val) => updateDraft('about.italicTitle', val)}
                  />
                  <ImageUploaderGroup
                    label="Foto Cerita Kami"
                    value={draftContent.about.image}
                    fieldPath="about.image"
                    onChange={(val) => updateDraft('about.image', val)}
                  />
                  <InputGroup
                    label="Kutipan Inspiratif (Quote)"
                    value={draftContent.about.quote}
                    onChange={(val) => updateDraft('about.quote', val)}
                  />

                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-[#8A6E52] mb-1.5">
                      Cerita / Paragraf Deskripsi (2 baris baru per paragraf)
                    </label>
                    <textarea
                      value={draftContent.about.paragraphs?.join('\n\n') || ''}
                      onChange={(e) => {
                        updateDraft('about.paragraphs', e.target.value.split('\n\n').filter(x => x.trim() !== ''));
                      }}
                      rows={6}
                      placeholder="Tuliskan setiap cerita..."
                      className="w-full bg-[#FAF8F5] border border-[#E8E3D9] text-[#1A1A1A] text-sm px-4 py-2.5 rounded-md focus:outline-none"
                    />
                  </div>
                </div>
              )}

              {/* --- TESTIMONIALS TAB --- */}
              {activeTab === 'testimonials' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-serif-display text-lg text-[#1A1A1A] border-b border-[#E8E3D9] pb-2 mb-4">
                      Header Bagian Bukti Nyata
                    </h3>
                    <InputGroup
                      label="Overline"
                      value={draftContent.testimonialsSection?.overline}
                      onChange={(val) => updateDraft('testimonialsSection.overline', val)}
                    />
                    <InputGroup
                      label="Judul Utama"
                      value={draftContent.testimonialsSection?.title}
                      onChange={(val) => updateDraft('testimonialsSection.title', val)}
                    />
                    <InputGroup
                      label="Judul Miring"
                      value={draftContent.testimonialsSection?.italicTitle}
                      onChange={(val) => updateDraft('testimonialsSection.italicTitle', val)}
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center border-b border-[#E8E3D9] pb-2 mb-4">
                      <h3 className="font-serif-display text-lg text-[#1A1A1A]">Daftar Testimoni</h3>
                      <button
                        type="button"
                        onClick={addTestimonial}
                        className="text-xs bg-[#8A6E52] text-white px-3 py-1.5 rounded flex items-center gap-1 hover:bg-[#1A1A1A] transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" /> Tambah
                      </button>
                    </div>

                    <div className="space-y-4">
                      {draftContent.testimonials.map((t, idx) => (
                        <div key={idx} className="p-4 bg-[#FAF8F5] border border-[#E8E3D9] rounded-lg relative">
                          <button
                            type="button"
                            onClick={() => removeTestimonial(idx)}
                            className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                          <h4 className="font-serif-display text-md text-[#1A1A1A] mb-2">Testimoni #{idx + 1}</h4>
                          <TextareaGroup
                            label="Kutipan Testimoni (Quote)"
                            value={t.quote}
                            onChange={(val) => {
                              const testimonials = [...draftContent.testimonials];
                              testimonials[idx].quote = val;
                              updateDraft('testimonials', testimonials);
                            }}
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <InputGroup
                              label="Nama Pelanggan"
                              value={t.name}
                              onChange={(val) => {
                                const testimonials = [...draftContent.testimonials];
                                testimonials[idx].name = val;
                                updateDraft('testimonials', testimonials);
                              }}
                            />
                            <InputGroup
                              label="Keterangan / Lokasi"
                              value={t.role}
                              onChange={(val) => {
                                const testimonials = [...draftContent.testimonials];
                                testimonials[idx].role = val;
                                updateDraft('testimonials', testimonials);
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-48 text-[#8A6E52] text-sm gap-2">
              <div className="h-4 w-4 border-2 border-[#8A6E52] border-t-transparent animate-spin rounded-full" />
              Memuat data draf...
            </div>
          )}
        </div>

        {/* Action Footer */}
        <div className="p-6 border-t border-[#E8E3D9] bg-[#FAF8F5] flex justify-between items-center shrink-0">
          <div className="flex items-center gap-1.5 text-xs text-[#8A6E52]">
            <AlertCircle className="h-4 w-4 text-[#BCA38F]" />
            <span className="hidden sm:inline">Live-preview aktif</span>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2.5 rounded border border-[#E8E3D9] hover:bg-[#E8E3D9]/20 text-[#1A1A1A] text-xs font-semibold tracking-wider uppercase transition-colors"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-5 py-2.5 rounded bg-[#1A1A1A] text-white hover:bg-[#8A6E52] text-xs font-bold tracking-widest uppercase flex items-center gap-2 shadow-md transition-colors"
            >
              <Save className="h-4 w-4" />
              Simpan
            </button>
          </div>
        </div>
      </div>

      {/* RIGHT PREVIEW PANEL (DESKTOP ONLY) */}
      <div className="hidden lg:flex flex-1 flex-col h-full bg-[#FAF8F5] p-6 relative">
        <div className="absolute inset-0 bg-stone-100 pointer-events-none" />
        
        {/* Mock Browser Header */}
        <div className="bg-[#FDFBF7] border border-[#E8E3D9] border-b-0 rounded-t-lg px-4 py-3 flex items-center gap-3 z-10 shrink-0 shadow-sm">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-400/80" />
            <span className="w-3 h-3 rounded-full bg-yellow-400/80" />
            <span className="w-3 h-3 rounded-full bg-green-400/80" />
          </div>
          <div className="flex-1 max-w-md mx-auto bg-[#FAF8F5] border border-[#E8E3D9] rounded py-1 px-4 text-center text-xs text-[#5C5C5C] flex items-center justify-center gap-1.5 select-none font-mono tracking-tight">
            <Globe className="h-3 w-3 text-[#BCA38F]" />
            https://davm.co.id/
          </div>
          <div className="w-16 text-right text-[10px] text-[#8A6E52] uppercase font-bold tracking-widest">
            Pratinjau
          </div>
        </div>

        {/* Browser Content Canvas */}
        <div className="flex-1 bg-white border border-[#E8E3D9] rounded-b-lg overflow-hidden relative shadow-md z-10 flex flex-col">
          <div className="flex-1 overflow-y-auto overflow-x-hidden scroll-smooth">
            <Hero />
            <Products />
            <Benefits />
            <About />
            <Testimonials />
          </div>
        </div>
      </div>

    </div>
  );
};
